import { BillingQueues } from '../components/billing/BillingWorkflowTools'
import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  CircularProgress,
  Alert,
  LinearProgress,
  Link as JoyLink,
  Stack,
  Chip,
  IconButton,
} from '@mui/joy'
import { ResponsiveTable } from '../components/common/ResponsiveTable'
import {
  Warning,
  ArrowRight as ArrowForward,
  CurrencyDollar as AttachMoney,
  ClockClockwise as PendingActions,
  CheckCircle
} from '@phosphor-icons/react'
import { analyticsApi, invoicesApi } from '../lib/billingApi'
import { formatCurrency, getStatusColor } from '../lib/billingUtils'
import type { Invoice } from '../types/billing'
import { Bar, BarChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

// Metric Card Component - icon chip + uppercase label, large value, free-form footer
interface MetricCardProps {
  title: string
  value: string
  icon: React.ReactNode
  color?: 'primary' | 'success' | 'warning' | 'danger' | 'neutral'
  topRight?: React.ReactNode
  children?: React.ReactNode
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon,
  color = 'neutral',
  topRight,
  children,
}) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              bgcolor: `${color}.softBg`,
              color: `${color}.solidBg`,
            }}
          >
            {icon}
          </Box>
          <Typography
            level="body-xs"
            sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 'lg', color: 'text.secondary' }}
          >
            {title}
          </Typography>
        </Box>
        {topRight}
      </Box>
      <Typography level="h2" fontWeight="xl">{value}</Typography>
      {children}
    </CardContent>
  </Card>
)

export default function BillingDashboard() {
  // Fetch dashboard metrics
  const { data: metrics, isLoading: metricsLoading, error: metricsError } = useQuery({
    queryKey: ['billing-dashboard-metrics'],
    queryFn: analyticsApi.dashboard,
  })

  // Fetch recent invoices
  const { data: recentInvoices, isLoading: invoicesLoading } = useQuery({
    queryKey: ['billing-recent-invoices'],
    queryFn: () => invoicesApi.list({ status: 'sent' }),
  })

  if (metricsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (metricsError) {
    return (
      <Alert color="danger" sx={{ m: 2 }}>
        Error loading dashboard data. Please try again.
      </Alert>
    )
  }


  const outstandingAmount = parseFloat(String(metrics?.outstanding_balance || 0))
  const overdueAmount = parseFloat(String(metrics?.overdue_amount || 0))
  const agingCurrent = parseFloat(String(metrics?.aging_summary?.current || 0))
  const pctCurrent = outstandingAmount > 0
    ? Math.round((agingCurrent / outstandingAmount) * 100)
    : 100

  return (
    <Box sx={{ width: '100%', p: { xs: 1.5, md: 3 } }}>
      <BillingQueues/>
      {/* Metrics Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid xs={12} sm={6} lg={3}>
          <MetricCard
            title="Total Revenue"
            value={formatCurrency(metrics?.total_revenue || 0)}
            icon={<AttachMoney size={18} />}
            color="success"
          >
            <Typography level="body-sm" color="neutral">All-time collected</Typography>
          </MetricCard>
        </Grid>
        <Grid xs={12} sm={6} lg={3}>
          <MetricCard
            title="Outstanding Balance"
            value={formatCurrency(metrics?.outstanding_balance || 0)}
            icon={<PendingActions size={18} />}
            color="primary"
            topRight={
              <Chip size="sm" variant="soft" color="primary">
                {metrics?.invoices_sent || 0} invoice{(metrics?.invoices_sent || 0) === 1 ? '' : 's'}
              </Chip>
            }
          >
            <Box>
              <LinearProgress
                determinate
                value={pctCurrent}
                sx={{ mb: 0.75, '--LinearProgress-thickness': '6px' }}
              />
              <Typography level="body-xs" color="neutral">
                {pctCurrent}% within current terms
              </Typography>
              <JoyLink
                component={Link}
                to="/operations/billing/invoices"
                level="body-sm"
                endDecorator={<ArrowForward size={14} />}
                sx={{ mt: 0.75 }}
              >
                Review receivables
              </JoyLink>
            </Box>
          </MetricCard>
        </Grid>
        <Grid xs={12} sm={6} lg={3}>
          <MetricCard
            title="Overdue Amount"
            value={formatCurrency(metrics?.overdue_amount || 0)}
            icon={<Warning size={18} />}
            color={overdueAmount > 0 ? 'danger' : 'neutral'}
            topRight={
              overdueAmount > 0 ? (
                <Chip size="sm" variant="soft" color="danger">
                  {metrics?.invoices_overdue || 0} invoice{(metrics?.invoices_overdue || 0) === 1 ? '' : 's'}
                </Chip>
              ) : undefined
            }
          >
            {overdueAmount > 0 ? (
              <JoyLink
                component={Link}
                to="/operations/billing/invoices?status=overdue"
                level="body-sm"
                color="danger"
                endDecorator={<ArrowForward size={14} />}
              >
                View overdue invoices
              </JoyLink>
            ) : (
              <Box>
                <Typography level="body-sm" color="neutral">
                  No unpaid balance is currently beyond its due date.
                </Typography>
                <Chip
                  size="sm"
                  variant="soft"
                  color="success"
                  startDecorator={<CheckCircle size={14} />}
                  sx={{ mt: 0.75 }}
                >
                  Portfolio current
                </Chip>
              </Box>
            )}
          </MetricCard>
        </Grid>
        <Grid xs={12} sm={6} lg={3}>
          <MetricCard
            title="Paid This Month"
            value={formatCurrency(metrics?.paid_this_month || 0)}
            icon={<CheckCircle size={18} />}
            color="success"
            topRight={
              <Chip size="sm" variant="soft" color="success">
                {metrics?.invoices_paid || 0} paid
              </Chip>
            }
          >
            <Typography level="body-sm" color="neutral">Payments collected this month</Typography>
          </MetricCard>
        </Grid>
      </Grid>

      {/* Charts and Tables */}
      <Grid container spacing={3}>
        {/* Revenue Chart */}
        <Grid xs={12} lg={8}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="h4">Revenue Performance</Typography>
              <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Billed revenue collected by month.
              </Typography>
              {metrics?.revenue_by_month && metrics.revenue_by_month.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={metrics.revenue_by_month}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value as string)} />
                    <Bar dataKey="revenue" fill="var(--joy-palette-primary-500)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="neutral">No revenue data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Aging Summary */}
        <Grid xs={12} lg={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography level="h4">Receivables Aging</Typography>
              <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Open A/R: <Typography component="span" fontWeight="lg" sx={{ color: 'text.primary' }}>
                  {formatCurrency(metrics?.outstanding_balance || 0)}
                </Typography>
              </Typography>
              {metrics?.aging_summary ? (
                <Stack spacing={1.5}>
                  {([
                    { label: 'Current', queue: 'aging_current', amount: metrics.aging_summary.current, dot: 'var(--joy-palette-primary-500)' },
                    { label: '1–30 days', queue: 'aging_1_30', amount: metrics.aging_summary.days_1_30, dot: 'var(--joy-palette-warning-300)' },
                    { label: '31–60 days', queue: 'aging_31_60', amount: metrics.aging_summary.days_31_60, dot: 'var(--joy-palette-warning-500)' },
                    { label: '61–90 days', queue: 'aging_61_90', amount: metrics.aging_summary.days_61_90, dot: 'var(--joy-palette-danger-400)' },
                    { label: 'Over 90 days', queue: 'aging_over_90', amount: metrics.aging_summary.days_over_90, dot: 'var(--joy-palette-danger-600)' },
                  ] as const).map((bucket) => {
                    const amount = parseFloat(String(bucket.amount || 0))
                    return (
                      <Box key={bucket.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: bucket.dot }} />
                          <Button component={Link} to={`/operations/billing/invoices?queue=${bucket.queue}`} variant="plain" size="sm">{bucket.label}</Button>
                        </Box>
                        <Typography
                          level="body-sm"
                          fontWeight="lg"
                          sx={{ color: amount > 0 && bucket.label !== 'Current' ? 'danger.plainColor' : 'text.primary' }}
                        >
                          {formatCurrency(bucket.amount)}
                        </Typography>
                      </Box>
                    )
                  })}
                  {outstandingAmount > 0 && pctCurrent === 100 && (
                    <Chip size="sm" variant="soft" color="success" startDecorator={<CheckCircle size={14} />}>
                      100% within current terms
                    </Chip>
                  )}
                </Stack>
              ) : (
                <Typography color="neutral">No aging data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Invoices */}
        <Grid xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 3 }}
              >
                <Typography level="h4">Recent Invoices</Typography>
                <Button
                  component={Link}
                  to="/operations/billing/invoices"
                  endDecorator={<ArrowForward />}
                  variant="plain"
                  size="sm"
                >
                  View All
                </Button>
              </Stack>

              {invoicesLoading ? (
                <CircularProgress />
              ) : recentInvoices?.results && recentInvoices.results.length > 0 ? (
                <ResponsiveTable<Invoice>
                  rows={recentInvoices.results.slice(0, 5)}
                  getRowKey={(invoice) => invoice.id}
                  columns={[
                    {
                      key: 'invoice_number',
                      sortValue: (row) => row.document_number,
                      header: 'Invoice #',
                      mobileVariant: 'header',
                      render: (invoice) => (
                        <Link to={`/operations/billing/invoices/${invoice.id}`}>
                          <Typography level="title-sm">{invoice.document_number}</Typography>
                        </Link>
                      ),
                    },
                    {
                      key: 'customer',
                      sortValue: (row) => row.contact_name,
                      header: 'Customer',
                      render: (invoice) => (
                        <Typography level="body-sm">{invoice.contact_name || 'N/A'}</Typography>
                      ),
                    },
                    {
                      key: 'amount',
                      sortValue: (row) => Number(row.total_amount),
                      header: 'Amount',
                      render: (invoice) => (
                        <Typography level="body-sm">{formatCurrency(invoice.total_amount)}</Typography>
                      ),
                    },
                    {
                      key: 'due_date',
                      sortValue: (row) => row.due_date ? new Date(row.due_date) : null,
                      header: 'Due Date',
                      render: (invoice) => (
                        <Typography level="body-sm">
                          {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}
                        </Typography>
                      ),
                    },
                    {
                      key: 'status',
                      header: 'Status',
                      render: (invoice) => (
                        <Chip size="sm" color={getStatusColor(invoice.status)} variant="soft">
                          {invoice.status}
                        </Chip>
                      ),
                    },
                  ]}
                  rowActions={(invoice) => (
                    <IconButton
                      component={Link}
                      to={`/operations/billing/invoices/${invoice.id}`}
                      size="sm"
                      variant="plain"
                    >
                      <ArrowForward />
                    </IconButton>
                  )}
                />
              ) : (
                <Typography color="neutral">No recent invoices</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Overdue Invoices Alert - driven by the dashboard metrics */}
        {(metrics?.invoices_overdue || 0) > 0 && (
          <Grid xs={12}>
            <Alert
              color="warning"
              startDecorator={<Warning />}
              endDecorator={
                <Button
                  component={Link}
                  to="/operations/billing/invoices?status=overdue"
                  size="sm"
                  variant="solid"
                  color="warning"
                >
                  View Overdue
                </Button>
              }
            >
              <Typography level="body-sm" sx={{ color: 'inherit' }}>
                You have {metrics?.invoices_overdue} overdue invoice
                {(metrics?.invoices_overdue || 0) > 1 ? 's' : ''} totaling{' '}
                {formatCurrency(metrics?.overdue_amount || 0)}.
              </Typography>
            </Alert>
          </Grid>
        )}

        {/* Top Customers */}
        {metrics?.top_customers && metrics.top_customers.length > 0 && (
          <Grid xs={12} lg={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography level="h4" sx={{ mb: 3 }}>Top Customers</Typography>
                <Stack spacing={2}>
                  {metrics.top_customers.slice(0, 5).map((customer, index) => (
                    <Box key={customer.contact_id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography level="body-sm" color="neutral">#{index + 1}</Typography>
                        <Typography level="body-md">{customer.contact_name}</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography level="body-md" fontWeight="lg">
                          {formatCurrency(customer.total_revenue)}
                        </Typography>
                        {parseFloat(customer.outstanding_balance) > 0 && (
                          <Typography level="body-sm" color="warning">
                            {formatCurrency(customer.outstanding_balance)} outstanding
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Quick Stats */}
        <Grid xs={12} lg={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="h4" sx={{ mb: 3 }}>Quick Stats</Typography>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography level="body-md">Average Payment Time</Typography>
                  <Typography level="body-md" fontWeight="lg">
                    {metrics?.average_payment_time || 0} days
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography level="body-md">Total Invoices</Typography>
                  <Typography level="body-md" fontWeight="lg">
                    {(metrics?.invoices_sent || 0) + (metrics?.invoices_paid || 0)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography level="body-md">Payment Success Rate</Typography>
                  <Typography level="body-md" fontWeight="lg" color="success">
                    {metrics && metrics.invoices_sent 
                      ? Math.round((metrics.invoices_paid / (metrics.invoices_sent + metrics.invoices_paid)) * 100)
                      : 0}%
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}